import React, { useMemo, useState } from 'react';
import { Dropdown, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { switchOrganizationPrimary } from '../../features/auth/authSlice';
import {
  getActiveOrganization,
  getActiveOrganizationId,
  normalizeOrganizations,
} from '../../utils/organizations';
import CreateOrganizationModal from './CreateOrganizationModal';

const SidebarOrganizationDropdown = ({ collapsed = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const organizations = useMemo(
    () => normalizeOrganizations(user?.organizations),
    [user?.organizations]
  );
  const activeOrg = useMemo(
    () => getActiveOrganization(organizations, user),
    [organizations, user]
  );
  const activeOrganizationId = useMemo(
    () => getActiveOrganizationId(user),
    [user]
  );

  if (!user) return null;

  const handleSwitch = async (organizationId) => {
    if (String(organizationId) === String(activeOrganizationId)) return;

    setSwitching(true);
    setError('');
    try {
      await dispatch(switchOrganizationPrimary(organizationId)).unwrap();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(
        typeof err === 'string'
          ? err
          : err?.message || 'Could not switch organization.'
      );
    } finally {
      setSwitching(false);
    }
  };

  const handleOpenCreateModal = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setShowCreateModal(true);
  };

  const handleOrganizationCreated = () => {
    navigate('/dashboard', { replace: true });
  };

  return (
    <>
      <div className="sidebar-org-switcher px-3 mb-2">
        <Dropdown align="start" className="w-100">
          <Dropdown.Toggle
            variant="light"
            className="sidebar-org-switcher-toggle w-100 d-flex align-items-center"
            disabled={switching}
            id="sidebar-organization-dropdown"
          >
            <i className="ri-building-2-line flex-shrink-0" />
            {!collapsed && (
              <>
                <span className="org-name ms-2" title={activeOrg?.name}>
                  {switching ? 'Switching...' : activeOrg?.name || 'Organization'}
                </span>
                {switching ? (
                  <Spinner animation="border" size="sm" className="ms-auto org-chevron" />
                ) : (
                  <i className="ri-arrow-down-s-line ms-auto org-chevron" />
                )}
              </>
            )}
          </Dropdown.Toggle>
          <Dropdown.Menu className="sidebar-org-switcher-menu w-100">
            <Dropdown.Item
              className="sidebar-org-switcher-new text-primary fw-medium"
              onClick={handleOpenCreateModal}
              disabled={switching}
            >
              + New Organization
            </Dropdown.Item>
            {organizations.length > 0 && <Dropdown.Divider />}
            {organizations.map((org) => {
              const isActive = String(org.id) === String(activeOrganizationId);
              return (
                <Dropdown.Item
                  key={org.id}
                  active={isActive}
                  disabled={switching || isActive}
                  onClick={() => handleSwitch(org.id)}
                >
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    <span className="text-truncate">{org.name}</span>
                    {isActive && <i className="ri-check-line flex-shrink-0" />}
                  </div>
                  {org.roles?.length > 0 && (
                    <small className="text-muted d-block">
                      {org.roles.join(', ')}
                    </small>
                  )}
                </Dropdown.Item>
              );
            })}
          </Dropdown.Menu>
        </Dropdown>
        {error && !collapsed && (
          <small className="text-danger d-block mt-1">{error}</small>
        )}
      </div>

      <CreateOrganizationModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onCreated={handleOrganizationCreated}
      />
    </>
  );
};

export default SidebarOrganizationDropdown;
